import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Paper,
  Box,
  IconButton,
  Typography,
  Slider,
  Stack,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Collapse, // Import Collapse
  Tooltip,
} from "@mui/material";
import {
  PlayArrow,
  Pause,
  SkipNext,
  SkipPrevious,
  Close,
  VolumeUp,
  VolumeOff,
  ExpandLess, // Import ExpandLess
  ExpandMore, // Import ExpandMore
  DragIndicator, // Import drag handle icon
} from "@mui/icons-material";
import { useDrag } from "react-dnd"; // Import useDrag

// Define or import ItemTypes
const ItemTypes = {
  STICKY_PLAYER: "sticky_player",
};

// Helper function to format time (More Robust)
const formatTime = (seconds) => {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) {
    return "--:--"; // Or return "00:00"
  }
  const date = new Date(0);
  date.setSeconds(seconds);
  try {
    return date.toISOString().substring(14, 19); // MM:SS format
  } catch (error) {
    console.error("Error formatting time:", error, "Input seconds:", seconds);
    return "--:--"; // Fallback on error
  }
};
// --- Configuration ---
// const IDLE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes in milliseconds
const IDLE_TIMEOUT_MS = 1 * 60 * 1000; // 5 minutes in milliseconds

const StickyAudioPlayer = ({
  allRecordings,
  currentPlayingId,
  onClose,
  onPlayRequest, // To switch tracks
  // Time tracking callbacks
  onTimeUpdate,
  onPlayCallback,
  onPauseCallback,
  onEndedCallback,
}) => {
  const audioRef = useRef(null);
  const idleTimerRef = useRef(null); // Ref to store the idle timeout ID
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showTrackList, setShowTrackList] = useState(false);

  // --- Drag and Drop State ---
  const [finalYOffset, setFinalYOffset] = useState(0);

  const [{ isDragging, dragOffset }, dragRef] = useDrag(
    () => ({
      type: ItemTypes.STICKY_PLAYER,
      item: { type: ItemTypes.STICKY_PLAYER },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
        dragOffset: monitor.getDifferenceFromInitialOffset(),
      }),
      end: (item, monitor) => {
        const offset = monitor.getDifferenceFromInitialOffset();
        if (offset) {
          setFinalYOffset((prev) => prev + offset.y);
        }
      },
    }),
    [finalYOffset]
  );

  const currentY =
    isDragging && dragOffset ? finalYOffset + dragOffset.y : finalYOffset;
  // --- End Drag and Drop ---

  const currentRecording = allRecordings.find((r) => r.id === currentPlayingId);
  const currentRecordingIndex = allRecordings.findIndex(
    (r) => r.id === currentPlayingId
  );

  const getRecordingSrc = (recordingId) => {
    if (!recordingId) return null;
    // Ensure process.env is accessible or replace with your actual logic
    const apiUrl = process.env.REACT_APP_API_URL || ""; // Provide a fallback if needed
    return `${apiUrl}/QAInbound/admin/${recordingId}/recording/download`;
  };

  // --- Idle Timer Management ---
  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      //   console.log("StickyPlayer: Clearing idle timer");
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const startIdleTimer = useCallback(() => {
    clearIdleTimer(); // Clear any existing timer first
    // console.log(`StickyPlayer: Starting idle timer (${IDLE_TIMEOUT_MS / 1000}s)`);
    idleTimerRef.current = setTimeout(() => {
      //   console.log("StickyPlayer: Idle timeout reached, closing player.");
      onClose(); // Call the close handler passed via props
    }, IDLE_TIMEOUT_MS);
  }, [clearIdleTimer, onClose]); // Include dependencies

  // Effect to handle source change and reset player state
  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement || !currentPlayingId) return;

    clearIdleTimer(); // Clear timer when track changes

    const src = getRecordingSrc(currentPlayingId);
    if (audioElement.src !== src) {
      audioElement.src = src;
      setIsLoading(true);
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      const timer = setTimeout(() => {
        audioElement.load();
        audioElement.play().catch((e) => {
          console.error("Autoplay failed:", e);
          // If autoplay fails, the player is now idle, start the timer
          // Wait a brief moment to ensure state updates settle
          setTimeout(() => {
            if (!isPlaying) {
              // Double check it didn't start playing by other means
              startIdleTimer();
            }
          }, 100);
        });
      }, 50);
      return () => clearTimeout(timer);
    } else {
      // If src is the same, ensure it plays if it wasn't already
      if (!isPlaying) {
        audioElement.play().catch((e) => {
          console.error("Play failed:", e);
          // Also start idle timer if forcing play fails
          setTimeout(() => {
            if (!isPlaying) {
              startIdleTimer();
            }
          }, 100);
        });
      } else {
        // If it *is* playing, ensure the idle timer is cleared
        clearIdleTimer();
      }
    }
    // Add startIdleTimer/clearIdleTimer
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlayingId, clearIdleTimer, startIdleTimer]); // Rerun when the ID changes or timer functions change

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      const newDuration = audioRef.current.duration;
      // Only update state if the duration is a valid, finite number
      if (Number.isFinite(newDuration)) {
        setDuration(newDuration);
      } else {
        console.warn(
          "Invalid duration received from audio metadata:",
          newDuration
        );
        setDuration(0); // Reset to 0 if invalid duration is detected
      }
      setIsLoading(false);

      // ... (rest of the existing logic for idle timer) ...
      if (!audioRef.current.isPlaying) {
        setTimeout(() => {
          if (audioRef.current && !audioRef.current.paused) {
            clearIdleTimer();
          } else if (audioRef.current && audioRef.current.paused) {
            startIdleTimer();
          }
        }, 100);
      }
    }
  }, [startIdleTimer, clearIdleTimer]); // Dependencies remain the same

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    setIsLoading(false);
    clearIdleTimer(); // Clear timer when playback starts
    if (audioRef.current && currentPlayingId) {
      onPlayCallback(audioRef.current, currentPlayingId);
    }
  }, [onPlayCallback, currentPlayingId, clearIdleTimer]); // Add clearIdleTimer

  const handlePause = useCallback(() => {
    setIsPlaying(false);
    startIdleTimer(); // Start timer when playback pauses
    if (audioRef.current && currentPlayingId) {
      onPauseCallback(audioRef.current, currentPlayingId);
    }
  }, [onPauseCallback, currentPlayingId, startIdleTimer]); // Add startIdleTimer

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    startIdleTimer(); // Start timer when playback ends
    if (audioRef.current && currentPlayingId) {
      onEndedCallback(audioRef.current, currentPlayingId);
    }
    // Optional: Auto-play next track? If so, handleNextTrack would call onPlayRequest,
    // which would trigger the useEffect[currentPlayingId] and clear/manage the timer.
    // handleNextTrack();
  }, [onEndedCallback, currentPlayingId, startIdleTimer]); // Add startIdleTimer

  const handleTimeUpdateInternal = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      if (isPlaying && currentPlayingId) {
        onTimeUpdate(audioRef.current, currentPlayingId);
        // Optional: Reset timer on timeupdate? Usually not needed, play/pause is enough.
        // clearIdleTimer(); // Could uncomment if you want *any* playback activity to reset timer
      }
    }
  }, [onTimeUpdate, currentPlayingId, isPlaying /*, clearIdleTimer */]);

  const handleVolumeChange = (event, newValue) => {
    const newVolume = newValue / 100;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
      audioRef.current.muted = newVolume === 0;
    }
    // Optional: Reset timer on volume change? Debatable, usually not considered "active use"
    // if (isPlaying) clearIdleTimer(); else startIdleTimer();
  };

  const toggleMute = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    if (audioRef.current) {
      audioRef.current.muted = newMutedState;
      if (!newMutedState && volume === 0) {
        setVolume(1);
        audioRef.current.volume = 1;
      } else if (newMutedState) {
        setVolume(0);
      }
    }
    // Optional: Reset timer on mute toggle?
    // if (isPlaying) clearIdleTimer(); else startIdleTimer();
  };

  const handleSeek = (event, newValue) => {
    if (audioRef.current) {
      const wasPlaying = !audioRef.current.paused; // Check actual state before potential pause
      if (wasPlaying) audioRef.current.pause(); // Pause briefly if needed

      const newTime = (newValue / 100) * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);

      if (wasPlaying) {
        audioRef.current
          .play()
          .catch((e) => console.error("Seek play failed:", e));
        // Play will trigger handlePlay, clearing the timer
      } else {
        // If paused, seeking implies user interaction but doesn't start playback.
        // We should restart the idle timer because the user *did* something.
        startIdleTimer();
      }

      // Capture time immediately after seek if paused
      if (!wasPlaying && currentPlayingId) {
        onPauseCallback(audioRef.current, currentPlayingId);
      }
    }
  };

  // --- Control Handlers ---
  const handleTogglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      // Corresponds to !audioRef.current.paused checking actual state
      audioRef.current.pause(); // Will trigger handlePause -> startIdleTimer
    } else {
      audioRef.current.play().catch((e) => {
        console.error("Toggle play failed:", e);
        // If play fails, ensure timer is started (handlePlay won't fire)
        startIdleTimer();
      }); // Will trigger handlePlay -> clearIdleTimer
    }
  };

  // These handlers indirectly manage the timer via the useEffect[currentPlayingId]
  const handleNextTrack = () => {
    const nextIndex = (currentRecordingIndex + 1) % allRecordings.length;
    onPlayRequest(allRecordings[nextIndex].id);
  };

  const handlePreviousTrack = () => {
    const prevIndex =
      (currentRecordingIndex - 1 + allRecordings.length) % allRecordings.length;
    onPlayRequest(allRecordings[prevIndex].id);
  };

  // Connect audio events and manage timer cleanup
  useEffect(() => {
    const audioElement = audioRef.current;
    if (audioElement) {
      audioElement.addEventListener("loadedmetadata", handleLoadedMetadata);
      audioElement.addEventListener("play", handlePlay);
      audioElement.addEventListener("pause", handlePause);
      audioElement.addEventListener("ended", handleEnded);
      audioElement.addEventListener("timeupdate", handleTimeUpdateInternal);
      audioElement.addEventListener("canplay", () => setIsLoading(false));

      // Initial state check: If loaded and paused, start timer
      if (!isLoading && audioElement.paused && currentPlayingId) {
        startIdleTimer();
      }

      // Cleanup function
      return () => {
        // console.log("StickyPlayer: Removing event listeners and clearing timer on unmount");
        audioElement.removeEventListener(
          "loadedmetadata",
          handleLoadedMetadata
        );
        audioElement.removeEventListener("play", handlePlay);
        audioElement.removeEventListener("pause", handlePause);
        audioElement.removeEventListener("ended", handleEnded);
        audioElement.removeEventListener(
          "timeupdate",
          handleTimeUpdateInternal
        );
        audioElement.removeEventListener("canplay", () => setIsLoading(false));
        clearIdleTimer(); // Crucial: clear timer on component unmount
      };
    }
  }, [
    handleLoadedMetadata,
    handlePlay,
    handlePause,
    handleEnded,
    handleTimeUpdateInternal,
    clearIdleTimer, // Add clearIdleTimer to dependency array
    startIdleTimer, // Add startIdleTimer
    isLoading, // Add isLoading for the initial check
    currentPlayingId, // Add currentPlayingId for the initial check
  ]);

  // Also clear timer if the component is explicitly closed via the button
  const handleClose = () => {
    clearIdleTimer();
    onClose();
  };

  return (
    <Paper
      elevation={10}
      sx={{
        position: "fixed",
        bottom: "64px",
        left: 0,
        right: 0,

        borderTop: "1px solid",
        borderColor: "divider",
        ml: 7,
        zIndex: 9999, // Ensures it's visually on top
        backgroundColor: "background.paper",
        transform: `translateY(${currentY}px)`,
        transition: isDragging ? "none" : "transform 0.1s ease-out",
        opacity: isDragging ? 0.65 : 0.9,
      }}
    >
      {/* Drag Handle */}
      <Box
        ref={dragRef}
        sx={{
          py: 0.5,
          cursor: isDragging ? "grabbing" : "grab",
          textAlign: "center",
          backgroundColor: "action.selected",
          borderBottom: "1px solid",
          borderColor: "divider",
          lineHeight: 0,
          touchAction: "none",
        }}
      >
        <DragIndicator fontSize="small" sx={{ color: "text.secondary" }} />
      </Box>

      {/* Loading indicator */}
      {isLoading && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: 0, // Adjust if drag handle changes position
            left: 0,
            right: 0,
            height: "2px",
          }}
        />
      )}

      <Box sx={{ p: 1.5 }}>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          justifyContent="space-between"
        >
          {/* Track Info & List Toggle */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ minWidth: 250, overflow: "hidden" }}
          >
            {/* ... (Tooltip and IconButton for track list) */}
            <Tooltip
              title={showTrackList ? "Hide Track List" : "Show Track List"}
            >
              <IconButton
                size="small"
                onClick={() => setShowTrackList(!showTrackList)}
              >
                {showTrackList ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Tooltip>
            <Tooltip title={currentRecording?.name ?? "Loading..."}>
              <Typography variant="body2" noWrap sx={{ flexGrow: 1 }}>
                {currentRecording?.name ?? "Loading..."}
              </Typography>
            </Tooltip>
          </Stack>

          {/* Main Controls */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexGrow={1}
            justifyContent="center"
          >
            {/* ... (Previous, Play/Pause, Next buttons) */}
            <Tooltip title="Previous Track">
              <IconButton
                size="small"
                onClick={handlePreviousTrack}
                disabled={allRecordings.length <= 1}
              >
                <SkipPrevious />
              </IconButton>
            </Tooltip>
            <IconButton
              size="medium"
              onClick={handleTogglePlay}
              disabled={isLoading || !currentPlayingId}
            >
              {isPlaying ? (
                <Pause fontSize="large" />
              ) : (
                <PlayArrow fontSize="large" />
              )}
            </IconButton>
            <Tooltip title="Next Track">
              <IconButton
                size="small"
                onClick={handleNextTrack}
                disabled={allRecordings.length <= 1}
              >
                <SkipNext />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Time & Seek */}
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ minWidth: 650, flexShrink: 0 }}
          >
            {/* ... (Time display and Slider) */}
            <Typography variant="caption" sx={{ minWidth: "40px" }}>
              {formatTime(currentTime)}
            </Typography>
            <Slider
              size="small"
              value={duration ? (currentTime / duration) * 100 : 0}
              onChange={handleSeek}
              aria-labelledby="seek-slider"
              disabled={isLoading || !duration}
              sx={{ flexGrow: 1 }}
            />
            <Typography variant="caption" sx={{ minWidth: "40px" }}>
              {formatTime(duration)}
            </Typography>
          </Stack>

          {/* Volume & Close */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{ minWidth: 250 }}
          >
            {/* ... (Volume controls) */}
            <Tooltip title={isMuted ? "Unmute" : "Mute"}>
              <IconButton size="small" onClick={toggleMute}>
                {isMuted || volume === 0 ? <VolumeOff /> : <VolumeUp />}
              </IconButton>
            </Tooltip>
            <Slider
              size="small"
              value={isMuted ? 0 : volume * 100}
              onChange={handleVolumeChange}
              aria-labelledby="volume-slider"
              sx={{ width: 80 }}
            />
            {/* Use the wrapper handleClose here */}
            <Tooltip title="Close Player">
              <IconButton size="small" onClick={handleClose}>
                <Close />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Collapsible Track List */}
        <Collapse in={showTrackList}>
          {/* ... (Track list implementation remains the same) ... */}
          <Box
            sx={{
              maxHeight: 150,
              overflowY: "auto",
              borderTop: "1px solid",
              borderColor: "divider",
              mt: 1,
              pt: 1,
            }}
          >
            <Typography variant="overline" sx={{ px: 1 }}>
              Available Recordings
            </Typography>
            <List dense disablePadding>
              {allRecordings.map((rec, index) => (
                <ListItem
                  key={rec.id}
                  disablePadding
                  secondaryAction={
                    rec.id === currentPlayingId && isPlaying ? (
                      <Pause fontSize="small" />
                    ) : rec.id === currentPlayingId ? (
                      <PlayArrow fontSize="small" />
                    ) : null
                  }
                >
                  <ListItemButton
                    selected={rec.id === currentPlayingId}
                    onClick={() => onPlayRequest(rec.id)} // Triggers useEffect[currentPlayingId] -> manages timer
                  >
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <Typography variant="caption">{index + 1}.</Typography>
                    </ListItemIcon>
                    <ListItemText
                      primary={rec.name || `Recording ${index + 1}`}
                      primaryTypographyProps={{
                        variant: "body2",
                        noWrap: true,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Collapse>
      </Box>

      {/* Hidden audio element */}
      <audio ref={audioRef} preload="metadata" />
    </Paper>
  );
};

export default StickyAudioPlayer;
